import { Fragment, useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Reveal } from '../../components/Reveal';
import { img, type PhotoKey } from '../../content/imagery';
import type { Article } from '../../content/inspiration';
import { OCCASIONS } from '../../content/occasions';
import { useT, type Key, type Lang } from '../../i18n';
import './pages.css';

/**
 * Building blocks shared by the content pages (event types, locations,
 * inspiration, about, contact, privacy): the night page hero, the closing help
 * band, the not-found state, the guide cards and the event-type picker.
 */

/** A guide's text in the reader's language (Malayalam where it exists). */
export const pick = (lang: Lang, text: { en: string; ml: string }) => (lang === 'ml' && text.ml ? text.ml : text.en);

export const ARTICLE_CATEGORY_KEY: Record<Article['category'], Key> = {
  planning: 'pgCatPlanning',
  weddings: 'pgCatWeddings',
  corporate: 'pgCatCorporate',
  venues: 'pgCatVenues',
};

const fade = (d: number) => ({ '--d': `${d}s` }) as CSSProperties;

/** The last word of a heading lit gold, as the design's titles are. */
export function Accent({ text }: { text: string }) {
  const s = text.trim();
  const i = s.lastIndexOf(' ');
  if (i < 0) return <span className="accent">{s}</span>;
  return (
    <>
      {s.slice(0, i)} <span className="accent">{s.slice(i + 1)}</span>
    </>
  );
}

/**
 * A translated sentence with one inserted value lit gold — word order differs
 * between English and Malayalam, so the value is placed by the phrase itself.
 */
export const ACCENT_MARK = '\u0000';
export function AccentValue({ phrase, value }: { phrase: string; value: string }) {
  const parts = phrase.split(ACCENT_MARK);
  if (parts.length < 2) return <>{phrase}</>;
  return (
    <>
      {parts.map((p, i) => (
        <Fragment key={i}>
          {p}
          {i < parts.length - 1 && <span className="accent">{value}</span>}
        </Fragment>
      ))}
    </>
  );
}

interface Crumb {
  to?: string;
  label: string;
}

/**
 * The night band every content page opens with (the design's .page-hero). It
 * runs under the transparent header; a photo, when given, sits on the right
 * behind a gradient. The entrance is CSS-only (.fade-up).
 */
export function PageHero({
  photo,
  photoSrc,
  kicker,
  title,
  lead,
  crumbs,
  children,
  note,
}: {
  photo?: PhotoKey;
  /** A photo URL that is not editorial imagery (a venue's own photo). */
  photoSrc?: string;
  kicker?: ReactNode;
  title: ReactNode;
  lead?: ReactNode;
  crumbs?: Crumb[];
  /** Call-to-action buttons. */
  children?: ReactNode;
  /** A small line under the actions (opening hours, last updated…). */
  note?: ReactNode;
}) {
  const { t } = useT();
  const hasImg = Boolean(photo || photoSrc);
  return (
    <section className={`page-hero${hasImg ? ' has-img' : ''}`}>
      {photo ? (
        <img
          key={photo}
          className="ph-img pg-ph-in"
          src={img(photo, 1600, { h: 900 })}
          srcSet={`${img(photo, 960, { h: 540 })} 960w, ${img(photo, 1600, { h: 900 })} 1600w`}
          sizes="(max-width: 1080px) 100vw, 60vw"
          alt=""
          {...{ fetchpriority: 'high' }}
        />
      ) : photoSrc ? (
        <img className="ph-img pg-ph-in" src={photoSrc} alt="" {...{ fetchpriority: 'high' }} />
      ) : null}
      <div className="wrap">
        {crumbs && crumbs.length > 0 && (
          <nav className="crumbs fade-up" style={fade(0)} aria-label={t('pgBreadcrumb')}>
            {crumbs.map((c, i) => (
              <Fragment key={i}>
                {i > 0 && <i className="ri-arrow-right-s-line i" aria-hidden="true" />}
                {c.to ? <Link to={c.to}>{c.label}</Link> : <span aria-current="page">{c.label}</span>}
              </Fragment>
            ))}
          </nav>
        )}
        {kicker && (
          <span className="kicker fade-up" style={fade(0.1)}>
            {kicker}
          </span>
        )}
        <div className="fade-up" style={fade(0.18)}>
          <h1>{title}</h1>
        </div>
        {lead && (
          <div className="lede fade-up" style={fade(0.28)}>
            <p>{lead}</p>
          </div>
        )}
        {children && (
          <div className="hero-ctas fade-up" style={fade(0.38)}>
            {children}
          </div>
        )}
        {note && (
          <div className="pg-hero-note fade-up" style={fade(0.45)}>
            {note}
          </div>
        )}
      </div>
    </section>
  );
}

/** The soft gold closing band: one line of encouragement and the next step. */
export function HelpBand({ title, body, children }: { title: ReactNode; body?: ReactNode; children: ReactNode }) {
  return (
    <section className="section is-tight">
      <div className="wrap">
        <Reveal className="help-band">
          <div>
            <h2>{title}</h2>
            {body && <p>{body}</p>}
          </div>
          <div className="hero-ctas">{children}</div>
        </Reveal>
      </div>
    </section>
  );
}

/** A friendly dead end for an unknown slug, with the way back. */
export function NotFound({ title, body, to, label }: { title: string; body: string; to: string; label: string }) {
  const { t } = useT();
  return (
    <PageHero
      kicker="404"
      title={<Accent text={title} />}
      lead={body}
      crumbs={[{ to: '/', label: t('snHome') }, { label: title }]}
    >
      <Link className="btn btn-primary" to={to}>
        <i className="ri-arrow-left-line i" aria-hidden="true" /> {label}
      </Link>
    </PageHero>
  );
}

/** A guide as a card (the design's .guide-card): photo, topic, time, title, summary. */
export function ArticleCard({ article, headingLevel = 3 }: { article: Article; headingLevel?: 2 | 3 }) {
  const { t, lang } = useT();
  const H = headingLevel === 2 ? 'h2' : 'h3';
  return (
    <article className="guide-card">
      <div className="g-media">
        <img
          src={img(article.photo, 800, { h: 600 })}
          srcSet={`${img(article.photo, 480, { h: 360 })} 480w, ${img(article.photo, 800, { h: 600 })} 800w`}
          sizes="(max-width: 720px) 100vw, (max-width: 1080px) 50vw, 33vw"
          alt=""
          loading="lazy"
          decoding="async"
        />
      </div>
      <div className="g-meta">
        <span>{t(ARTICLE_CATEGORY_KEY[article.category])}</span>
        <span>{t('pgReadMins', { count: article.readMins })}</span>
      </div>
      <H className="pg-gc-title">
        <Link to={`/inspiration/${article.slug}`}>{pick(lang, article.title)}</Link>
      </H>
      <p>{pick(lang, article.excerpt)}</p>
      <span className="link-arrow" aria-hidden="true">
        <span>{t('pgReadGuide')}</span>
        <i className="ri-arrow-right-line i" />
      </span>
    </article>
  );
}

/** The featured guide, wide (the design's .guide-hero). */
export function ArticleHero({ article }: { article: Article }) {
  const { t, lang } = useT();
  return (
    <article className="guide-hero">
      <div className="g-media">
        <img
          src={img(article.photo, 1200, { h: 800 })}
          srcSet={`${img(article.photo, 720, { h: 480 })} 720w, ${img(article.photo, 1200, { h: 800 })} 1200w`}
          sizes="(max-width: 1080px) 100vw, 55vw"
          alt=""
          loading="lazy"
          decoding="async"
        />
      </div>
      <div className="g-body">
        <span className="badge">
          <i className="ri-sparkling-2-line i" aria-hidden="true" />
          {t('pgFeatured')}
        </span>
        <div className="g-meta">
          <span>{t(ARTICLE_CATEGORY_KEY[article.category])}</span>
          <span>{t('pgReadMins', { count: article.readMins })}</span>
        </div>
        <h2>
          <Link to={`/inspiration/${article.slug}`}>{pick(lang, article.title)}</Link>
        </h2>
        <p>{pick(lang, article.excerpt)}</p>
        <span className="btn btn-primary pg-self-start" aria-hidden="true">
          {t('pgReadGuide')}
          <i className="ri-arrow-right-line i i-move" />
        </span>
      </div>
    </article>
  );
}

/**
 * The sticky row of event-type chips under the hero: "All event types" and
 * one per occasion. The current one is marked as the page.
 */
export function OccasionPicker({ current }: { current?: string }) {
  const { t } = useT();
  const ref = useRef<HTMLDivElement>(null);

  // Keep the selected chip in view when the row scrolls sideways (phones).
  useEffect(() => {
    const el = ref.current?.querySelector<HTMLElement>('[aria-current="page"]');
    if (el && ref.current) ref.current.scrollLeft = el.offsetLeft - 24;
  }, [current]);

  return (
    <nav className="occ-picker" aria-label={t('pgOccPicker')}>
      <div className="wrap">
        <div className="occ-switch" ref={ref}>
          <Link className="chip" to="/occasions" aria-current={!current ? 'page' : undefined}>
            {t('pgAllTypesTitle')}
          </Link>
          {OCCASIONS.map((o) => (
            <Link
              key={o.slug}
              className="chip"
              to={`/occasions/${o.slug}`}
              aria-current={current === o.slug ? 'page' : undefined}
            >
              {t(o.title)}
            </Link>
          ))}
        </div>
      </div>
    </nav>
  );
}

/**
 * A figure that counts up from zero the first time it scrolls into view. The
 * final value is in the markup from the start, so without IntersectionObserver
 * or with reduced motion it simply shows.
 */
export function CountUp({ value, decimals = 0, suffix = '' }: { value: number; decimals?: number; suffix?: string }) {
  const { locale } = useT();
  const ref = useRef<HTMLDivElement>(null);
  const format = (v: number) =>
    v.toLocaleString(locale, { minimumFractionDigits: decimals, maximumFractionDigits: decimals }) + suffix;
  const [text, setText] = useState(() => format(value));

  useEffect(() => {
    setText(format(value));
    const el = ref.current;
    const still = typeof matchMedia !== 'undefined' && matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!el || still || typeof IntersectionObserver === 'undefined') return;
    let raf = 0;
    const io = new IntersectionObserver(
      ([en]) => {
        if (!en.isIntersecting) return;
        io.disconnect();
        const t0 = performance.now();
        const step = (now: number) => {
          const p = Math.min(1, (now - t0) / 1600);
          setText(format(value * (1 - Math.pow(1 - p, 4))));
          if (p < 1) raf = requestAnimationFrame(step);
        };
        raf = requestAnimationFrame(step);
      },
      { threshold: 0.5 },
    );
    io.observe(el);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, decimals, suffix, locale]);

  return (
    <div className="num" ref={ref}>
      {text}
    </div>
  );
}

/**
 * Scroll to the element named in the URL's #hash once the page has rendered.
 *
 * The router does not do this on its own, and ScrollToTop puts every new page
 * at the top — so a footer link to /about#faq would otherwise land on the
 * hero. A second pass after the photos settle corrects for layout shift. The
 * target's scroll-margin-top keeps it clear of the fixed header.
 */
export function useHashScroll() {
  const { hash, key } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const id = decodeURIComponent(hash.slice(1));
    const go = () => document.getElementById(id)?.scrollIntoView({ block: 'start' });
    const timers = [window.setTimeout(go, 0), window.setTimeout(go, 400)];
    return () => timers.forEach((tm) => window.clearTimeout(tm));
  }, [hash, key]);
}
